import { ProductSummary } from '@core/models/product-summary.model';
import { Product, getDdqIssuerUidFromProduct, getDdqProductUidFromProduct } from '@core/models/product.model';

const CLIENTS_DASHBOARD_DDQ_PRODUCT_PATH =
  /\/clients\/[^/]+\/dashboard\/(?:fund-ddq-runs|ddq-invites|marketplace)\/[^/]+\/ddq(\/|$)/;

const CLIENTS_DASHBOARD_SHELL_PATH = /\/clients\/[^/]+\/dashboard(\/|$)/;

/**
 * Client dashboard tabs (marketplace, baskets, ddq-invites list, etc.) that must use
 * investor-scoped APIs — not emittent namespace left over from fund-ddq-runs / DDQ views.
 */
export function isClientInvestorDashboardShellPath(url: string | null | undefined): boolean {
  const pathOnly = (url ?? '').split('?')[0];
  if (!CLIENTS_DASHBOARD_SHELL_PATH.test(pathOnly)) {
    return false;
  }
  return !isClientDdqProductUrlPath(pathOnly);
}

/** Client investor DDQ shell under `/client/.../ddq` or clients dashboard DDQ tab product routes. */
export function isClientDdqProductUrlPath(url: string | null | undefined): boolean {
  const pathOnly = (url ?? '').split('?')[0];
  return /\/client\/[^/]+\/ddq(\/|$)/.test(pathOnly) || CLIENTS_DASHBOARD_DDQ_PRODUCT_PATH.test(pathOnly);
}

export function getClientDdqRouteProductUidFromUrl(url: string | null | undefined): string | null {
  const pathOnly = (url ?? '').split('?')[0];
  const clientUserMatch = pathOnly.match(/\/client\/([^/]+)\/ddq(\/|$)/);
  const dashboardMatch = pathOnly.match(
    /\/clients\/[^/]+\/dashboard\/(?:fund-ddq-runs|ddq-invites|marketplace)\/([^/]+)\/ddq(\/|$)/,
  );
  const routeProductUid = clientUserMatch?.[1] ?? dashboardMatch?.[1] ?? null;
  if (!routeProductUid) {
    return null;
  }
  const value = decodeURIComponent(routeProductUid).trim();
  return value === '' ? null : value;
}

/** Route path segments (no leading slash), as built by product-summary resolver. */
export function isClientDdqShellRoutePath(path: string): boolean {
  return (
    /^client\/[^/]+\/ddq(\/|$)/.test(path) ||
    /^clients\/[^/]+\/dashboard\/(?:fund-ddq-runs|ddq-invites|marketplace)\/[^/]+\/ddq(\/|$)/.test(path)
  );
}

/**
 * When an investor views DDQ under `/client/.../ddq`, product-scoped APIs must use the issuer
 * namespace (`emittents/{issuerUid}/products/{issuerProductUid}`) from `productOpening.ddqSummary`,
 * not `investors/{currentOrg}` — even if `OrganizationContextService` was cleared or never set.
 */
export function resolveClientDdqEmittentApiScopeFromSummaries(
  organizationType: string | null | undefined,
  summaries: ReadonlyArray<Pick<ProductSummary, 'productOpening'>>,
): { issuerUid: string; ddqProductUid: string | null } | null {
  if (organizationType !== 'investor') {
    return null;
  }
  for (let i = summaries.length - 1; i >= 0; i--) {
    const opening = summaries[i]?.productOpening as Product | undefined;
    const issuerUid = getDdqIssuerUidFromProduct(opening);
    if (!issuerUid) {
      continue;
    }
    const ddqProductUid = getDdqProductUidFromProduct(opening);
    return { issuerUid, ddqProductUid };
  }
  return null;
}
